import * as React from 'react';
import { css } from '@emotion/react';
import { createChainedFunction, FixMeLater, startDirection } from '../../utils';
import { OptionList, OptionListProps } from '../OptionList/OptionList';
import { OptionListItem, OptionListItemProps } from '../OptionList/OptionListItem';
import { Option, OptionProps } from '../Option/Option';
import { usePrevious } from '../../hooks';
import { OptionMenuContext } from './OptionMenuContext';

export type OptionMenuProps = {
    /**
     * The content of the component.
     */
    children: React.ReactNode;

    /**
     * If `true`, the menu is visible.
     */
    open?: boolean;

    onClose?: () => void;

    /**
     * If `true`, the menu will behave as a submenu.
     */
    submenu?: boolean;

    title: string;

    depth?: number;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
};

export function OptionMenu(props: Readonly<OptionMenuProps>): React.JSX.Element {
    const { children, open = false, onClose, submenu, title, depth = 0, ref, ...other } = props;

    const { viewportDepth, animating, onMenuOpen } = React.useContext(OptionMenuContext);

    const [selectedOptionIndex, setSelectedOptionIndex] = React.useState<number | null>(null);

    const prevOpen = usePrevious(open);
    const prevAnimating = usePrevious(animating);

    const handleSubmenuClose = () => {
        setSelectedOptionIndex(null);
    };

    const handleSubmenuClick = (index: number) => () => {
        // An option with a submenu has been selected.
        // The submenu will be set to "open".
        setSelectedOptionIndex(index);
    };

    React.useEffect(() => {
        if (!prevOpen && open) {
            // This menu has been opened
            if (onMenuOpen) {
                onMenuOpen(depth, title);
            }
        }
    }, [depth, onMenuOpen, open, prevOpen, title]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        if (viewportDepth == null) {
            return;
        }
        if (prevAnimating && !animating) {
            // A menu animation just completed.
            // If the viewport depth is now less than the depth of this menu, then it needs to close.
            if (viewportDepth < depth) {
                if (onClose) {
                    onClose();
                }
            }
        }
    }, [animating, depth, onClose, open, prevAnimating, viewportDepth]);

    const submenuStyles = css`
        position: absolute;
        top: 0;
        ${startDirection()}: 100%;
        width: 100%;
        display: none;
        ${open &&
        `
                display: block;
            `}
    `;

    const renderList = React.useCallback(
        (optionList: React.ReactElement<OptionListProps>): React.ReactNode => {
            const listItems = React.Children.map(optionList.props.children, (listChild, listChildIndex) => {
                if (React.isValidElement(listChild)) {
                    if (listChild.type === OptionListItem) {
                        // This is an `OptionListItem`
                        const optionListItem = listChild as React.ReactElement<OptionListItemProps>;

                        // Does this ListItem contain a submenu?
                        const hasSubmenu = React.Children.toArray(optionListItem.props.children).some(
                            (element) => React.isValidElement(element) && element.type === OptionMenu
                        );

                        const submenuOpen = hasSubmenu && open && listChildIndex === selectedOptionIndex;

                        const listItemChildren = React.Children.map(optionListItem.props.children, (listItemChild) => {
                            if (React.isValidElement(listItemChild)) {
                                if (listItemChild.type === OptionMenu) {
                                    // This is an `OptionMenu`
                                    const optionMenu = listItemChild as React.ReactElement<OptionMenuProps>;

                                    return React.cloneElement(optionMenu, {
                                        submenu: true,
                                        depth: depth + 1,
                                        open: submenuOpen,
                                        onClose: handleSubmenuClose,
                                    });
                                }

                                if (listItemChild.type === Option) {
                                    // This is an `Option`
                                    const option = listItemChild as React.ReactElement<OptionProps>;

                                    const { onClick, heading } = option.props;

                                    const submenuClickHandler = onClick
                                        ? createChainedFunction(
                                              onClick as (this: unknown) => unknown,
                                              handleSubmenuClick(listChildIndex)
                                          )
                                        : handleSubmenuClick(listChildIndex);

                                    return React.cloneElement(listItemChild as React.ReactElement<OptionProps>, {
                                        role: !heading ? 'menuitem' : undefined,
                                        'aria-haspopup': hasSubmenu || undefined,
                                        'aria-expanded': hasSubmenu ? submenuOpen : undefined,
                                        tabIndex: !heading ? -1 : undefined,
                                        onClick: hasSubmenu ? submenuClickHandler : onClick,
                                    });
                                }

                                if (listItemChild.type === OptionList) {
                                    return renderList(listItemChild as React.ReactElement<OptionListProps>);
                                }

                                return listItemChild;
                            }
                            return null;
                        });

                        return React.cloneElement(optionListItem, {
                            children: listItemChildren,
                            role: 'none',
                        });
                    }
                }
                return listChild;
            });

            return React.cloneElement(optionList, {
                children: listItems,
                role: 'menu',
                'aria-label': title,
                'aria-expanded': open,
                ...other,
            });
        },
        [depth, open, other, selectedOptionIndex, title]
    );

    return (
        <div ref={ref} css={submenu ? submenuStyles : undefined} {...other}>
            {React.Children.map(children, (menuChild) => {
                // An `OptionMenu` can contain a single `OptionList`.
                if (React.isValidElement(menuChild)) {
                    if (menuChild && menuChild.type === OptionList) {
                        // This is an `OptionList`
                        const optionList = menuChild as React.ReactElement<OptionListProps>;
                        return renderList(optionList);
                    }
                }

                return null;
            })}
        </div>
    );
}
